import type { CustomFieldValues } from '@taki/data-model';
import type { TakiSdk } from '@taki/sdk';

/**
 * LE JEU DE DEMONSTRATION de l'app Projets : une agence de communication a
 * Luxembourg, 5 clients, 8 projets et 30 taches.
 *
 * L'ASYMETRIE QUE LE FONDATEUR A TRANCHEE, et chaque chiffre la sert :
 *
 *   Projets, Etape       -> 4 en cours, 2 en cadrage, 1 livre, 1 clos : le
 *                           kanban a une colonne pleine, deux moyennes, deux
 *                           a une carte, et « Annule » reste VIDE.
 *   Projets, Sante       -> 1 en retard (echeance passee), 1 en danger, le
 *                           reste en bonne voie ou sans valeur.
 *   Projets, Client      -> l'intranet de l'agence n'a PAS de client : la
 *                           colonne doit savoir se taire.
 *   Fiche projet, carte  -> « Refonte du site » porte 6 taches, l'intranet 1,
 *   « Taches »              le projet clos 1, et « Newsletter » 2 : jamais le
 *                           meme compte, et aucune carte n'est le jeu entier.
 *   Taches, sans projet  -> 6 sur 30 : la tache perso existe, et le favori
 *                           « Par projet » les regroupe en tete.
 *   Taches, echeances    -> 3 en retard (echeance passee, pas terminees), le
 *                           reste etale sur les trois semaines a venir, 3 sans
 *                           echeance.
 *   Taches, Assigne      -> Celia (l'owner du seed, celle qui ouvre l'app) en
 *                           porte 17, Marc 9, et 4 n'ont personne.
 *
 * LES REGLES DU PACK, tenues ici (les memes que `../timesheet/demo-dataset.ts`) :
 * - toutes les ecritures passent par `@taki/sdk`, aucun acces Firebase ;
 * - IDEMPOTENT : le SDK ne laisse pas choisir l'id d'un document, chaque ligne
 *   porte donc un `customFields.demoKey` stable (`pj-cl-*`, `pj-pr-*`,
 *   `pj-ta-*`). L'import ne cree QUE ce qui manque, et c'est aussi ce qui
 *   permet a une tache de pointer son projet sans connaitre son id ;
 * - PAS de suppression : pour repartir de zero, `pnpm dev:local --fresh` ;
 * - AUCUNE DATE FIGEE : tout se calcule a l'import depuis le lundi de la
 *   semaine courante, en heure LOCALE ;
 * - LES VALEURS SONT LES VALEURS STOCKEES du catalogue (`enCours`, `urgente`),
 *   jamais les libelles.
 *
 * L'ORDRE D'ECRITURE N'EST PAS LIBRE : les clients, puis les projets (un projet
 * pointe son client par `contactId`), puis les taches (une tache pointe son
 * projet par `projectId`). Chaque jeu se RELIT apres ecriture, et la table
 * `demoKey -> id` resout le lien. Une reference introuvable ne s'invente pas :
 * la fiche se cree sans elle.
 *
 * LES MAISONS SONT INVENTEES, et les deux personnes sont les comptes de
 * l'emulateur local (`scripts/compte-de-test.mjs`).
 */

const TYPE_SOCIETE = 'organization';
const TYPE_PROJET = 'projet';
const TYPE_TACHE = 'tache';

/** Les deux personnes, par leur ADRESSE ; ce qui s'ecrit est leur `personId`. */
const CELIA = 'test@taki.lu';
const MARC = 'membre@taki.lu';

/** Ce que l'import a fait, rendu au hub Parametres. */
export interface DemoImportResult {
  created: number;
  skipped: number;
  message: string;
}

/* ------------------------------------------------------------------ */
/* Les 5 clients                                                       */
/* ------------------------------------------------------------------ */

interface DemoClient {
  demoKey: string;
  name: string;
  email: string;
  phone: string;
}

export const DEMO_CLIENTS: ReadonlyArray<DemoClient> = [
  { demoKey: 'pj-cl-1', name: 'Moselle Immo SARL', email: 'contact@moselle-immo.lu', phone: '+352 26 58 14 00' },
  { demoKey: 'pj-cl-2', name: 'Banque Alzette SA', email: 'communication@banque-alzette.lu', phone: '+352 47 92 11 00' },
  { demoKey: 'pj-cl-3', name: 'Café Uelzecht', email: 'bonjour@cafe-uelzecht.lu', phone: '+352 621 48 33 12' },
  { demoKey: 'pj-cl-4', name: 'Fondation Kirchberg', email: 'info@fondation-kirchberg.lu', phone: '+352 43 21 70 50' },
  { demoKey: 'pj-cl-5', name: 'Commune de Differdange', email: 'presse@differdange.lu', phone: '+352 58 77 11 00' },
];

/* ------------------------------------------------------------------ */
/* Les 8 projets                                                       */
/* ------------------------------------------------------------------ */

interface DemoProjet {
  demoKey: string;
  libelle: string;
  /** La `demoKey` de son client ; absente = projet interne, sans client. */
  client?: string;
  etat: string;
  statutSuivi?: string;
  avancement: number;
  emailClient?: string;
  chef: string;
  etiquettes: string[];
  facturable: boolean;
  /** Debut et echeance, en jours depuis le lundi de la semaine courante. */
  debut: number;
  echeance: number;
  budgetHeures: number;
  budget?: number;
  description: string;
}

export const DEMO_PROJETS: ReadonlyArray<DemoProjet> = [
  {
    demoKey: 'pj-pr-1', libelle: 'Refonte du site Moselle Immo', client: 'pj-cl-1', etat: 'enCours',
    statutSuivi: 'enBonneVoie', avancement: 55, emailClient: 'jeanne.muller@moselle-immo.lu', chef: CELIA,
    etiquettes: ['externe', 'design'], facturable: true, debut: -35, echeance: 24, budgetHeures: 160, budget: 18500,
    description: 'Nouveau site vitrine : catalogue de biens, fiches détaillées, formulaire de contact et version allemande.',
  },
  /* LE PROJET EN RETARD : son echeance est passee de quatre jours. */
  {
    demoKey: 'pj-pr-2', libelle: 'Campagne printemps Banque Alzette', client: 'pj-cl-2', etat: 'enCours',
    statutSuivi: 'enRetard', avancement: 30, emailClient: 'luc.weber@banque-alzette.lu', chef: MARC,
    etiquettes: ['externe'], facturable: true, debut: -49, echeance: -4, budgetHeures: 90, budget: 12000,
    description: 'Affichage 4x3, bannières réseaux sociaux et spot radio de 30 secondes.',
  },
  /* LE PROJET EN DANGER. */
  {
    demoKey: 'pj-pr-3', libelle: 'Identité visuelle Café Uelzecht', client: 'pj-cl-3', etat: 'enCours',
    statutSuivi: 'enDanger', avancement: 70, emailClient: 'paul@cafe-uelzecht.lu', chef: CELIA,
    etiquettes: ['externe', 'design'], facturable: true, debut: -28, echeance: 6, budgetHeures: 40, budget: 4800,
    description: 'Logo, charte, menus et signalétique de la terrasse.',
  },
  {
    demoKey: 'pj-pr-4', libelle: 'Rapport annuel Fondation Kirchberg', client: 'pj-cl-4', etat: 'enCours',
    statutSuivi: 'enBonneVoie', avancement: 20, emailClient: 'info@fondation-kirchberg.lu', chef: MARC,
    etiquettes: ['externe'], facturable: true, debut: -10, echeance: 45, budgetHeures: 60, budget: 7200,
    description: 'Mise en page de 48 pages, version imprimée et PDF accessible.',
  },
  {
    demoKey: 'pj-pr-5', libelle: 'Stand ICT Spring', client: 'pj-cl-2', etat: 'cadrage',
    statutSuivi: 'enAttente', avancement: 5, chef: CELIA,
    etiquettes: ['externe', 'evolution'], facturable: true, debut: 7, echeance: 60, budgetHeures: 50,
    description: 'Stand de 18 m², kakémonos, écran et goodies.',
  },
  {
    demoKey: 'pj-pr-6', libelle: 'Newsletter mensuelle Differdange', client: 'pj-cl-5', etat: 'cadrage',
    avancement: 0, emailClient: 'presse@differdange.lu', chef: MARC,
    etiquettes: ['externe'], facturable: false, debut: 14, echeance: 75, budgetHeures: 24,
    description: 'Gabarit réutilisable et calendrier éditorial sur six mois.',
  },
  /* LE PROJET SANS CLIENT : un outil interne de l'agence. */
  {
    demoKey: 'pj-pr-7', libelle: "Intranet de l'agence", etat: 'livre',
    statutSuivi: 'enBonneVoie', avancement: 100, chef: CELIA,
    etiquettes: ['interne'], facturable: false, debut: -90, echeance: -12, budgetHeures: 30,
    description: "Outil interne : planning, annuaire et ressources de l'agence.",
  },
  {
    demoKey: 'pj-pr-8', libelle: 'Vidéo de marque Moselle Immo', client: 'pj-cl-1', etat: 'clos',
    avancement: 100, chef: MARC,
    etiquettes: ['externe'], facturable: true, debut: -120, echeance: -60, budgetHeures: 35, budget: 6000,
    description: 'Film de 90 secondes livré, droits musicaux réglés.',
  },
];

/* ------------------------------------------------------------------ */
/* Les 30 taches                                                       */
/* ------------------------------------------------------------------ */

interface DemoTache {
  demoKey: string;
  libelle: string;
  /** La `demoKey` de son projet ; absente = tache sans projet. */
  projet?: string;
  status: string;
  priority: string;
  /** L'adresse de l'assigne ; absente = personne. */
  assigne?: string;
  /** Echeance en jours depuis le lundi courant ; absente = sans echeance. */
  echeance?: number;
  validation?: string;
  etiquettes?: string[];
  tempsAlloue?: number;
}

export const DEMO_TACHES: ReadonlyArray<DemoTache> = [
  /* Refonte du site (6) */
  { demoKey: 'pj-ta-1', libelle: "Maquette de la page d'accueil", projet: 'pj-pr-1', status: 'enCours', priority: 'elevee', assigne: CELIA, echeance: 3, validation: 'changementsDemandes', etiquettes: ['design'], tempsAlloue: 240 },
  { demoKey: 'pj-ta-2', libelle: 'Gabarit des fiches de biens', projet: 'pj-pr-1', status: 'aFaire', priority: 'moyenne', assigne: CELIA, echeance: 8, etiquettes: ['design'], tempsAlloue: 180 },
  { demoKey: 'pj-ta-3', libelle: 'Formulaire de contact', projet: 'pj-pr-1', status: 'termine', priority: 'moyenne', assigne: MARC, echeance: -6, validation: 'approuvee', tempsAlloue: 120 },
  { demoKey: 'pj-ta-4', libelle: 'Mise en ligne et redirections', projet: 'pj-pr-1', status: 'aFaire', priority: 'urgente', assigne: MARC, echeance: 22, etiquettes: ['bug'], tempsAlloue: 90 },
  { demoKey: 'pj-ta-5', libelle: 'Traduction allemande du site', projet: 'pj-pr-1', status: 'aFaire', priority: 'moyenne', echeance: 18, etiquettes: ['evolution'], tempsAlloue: 240 },
  { demoKey: 'pj-ta-6', libelle: 'Photos des biens phares', projet: 'pj-pr-1', status: 'enCours', priority: 'faible', assigne: CELIA, echeance: 12, tempsAlloue: 300 },
  /* Campagne printemps (5), le projet en retard : deux taches le sont aussi. */
  { demoKey: 'pj-ta-7', libelle: 'Visuels affichage 4x3', projet: 'pj-pr-2', status: 'enCours', priority: 'urgente', assigne: MARC, echeance: -3, validation: 'enAttente', etiquettes: ['design'], tempsAlloue: 300 },
  { demoKey: 'pj-ta-8', libelle: 'Script du spot radio', projet: 'pj-pr-2', status: 'aFaire', priority: 'elevee', assigne: MARC, echeance: -1, tempsAlloue: 120 },
  { demoKey: 'pj-ta-9', libelle: 'Planning des publications', projet: 'pj-pr-2', status: 'aFaire', priority: 'urgente', assigne: CELIA, echeance: 2, tempsAlloue: 60 },
  { demoKey: 'pj-ta-10', libelle: 'Bannières réseaux sociaux', projet: 'pj-pr-2', status: 'termine', priority: 'moyenne', assigne: CELIA, echeance: -9, validation: 'approuvee', etiquettes: ['design'], tempsAlloue: 90 },
  { demoKey: 'pj-ta-11', libelle: 'Réservation des emplacements', projet: 'pj-pr-2', status: 'annule', priority: 'faible', echeance: -15, tempsAlloue: 30 },
  /* Identite visuelle (4) */
  { demoKey: 'pj-ta-12', libelle: 'Déclinaisons du logo', projet: 'pj-pr-3', status: 'enCours', priority: 'elevee', assigne: CELIA, echeance: 1, validation: 'changementsDemandes', etiquettes: ['design'], tempsAlloue: 180 },
  { demoKey: 'pj-ta-13', libelle: 'Menus de la terrasse', projet: 'pj-pr-3', status: 'aFaire', priority: 'moyenne', assigne: CELIA, echeance: 5, etiquettes: ['design'], tempsAlloue: 120 },
  { demoKey: 'pj-ta-14', libelle: 'Photos des plats', projet: 'pj-pr-3', status: 'enCours', priority: 'moyenne', assigne: MARC, echeance: 4, tempsAlloue: 240 },
  { demoKey: 'pj-ta-15', libelle: 'Devis signalétique', projet: 'pj-pr-3', status: 'aFaire', priority: 'faible', echeance: 6, tempsAlloue: 30 },
  /* Rapport annuel (3) */
  { demoKey: 'pj-ta-16', libelle: 'Chemin de fer du rapport', projet: 'pj-pr-4', status: 'termine', priority: 'moyenne', assigne: MARC, echeance: -2, validation: 'approuvee', tempsAlloue: 90 },
  { demoKey: 'pj-ta-17', libelle: 'Mise en page des chapitres 1 à 3', projet: 'pj-pr-4', status: 'enCours', priority: 'moyenne', assigne: CELIA, echeance: 15, etiquettes: ['design'], tempsAlloue: 360 },
  { demoKey: 'pj-ta-18', libelle: 'Relecture des textes', projet: 'pj-pr-4', status: 'aFaire', priority: 'faible', assigne: MARC, echeance: 20, tempsAlloue: 120 },
  /* Stand ICT Spring (2) */
  { demoKey: 'pj-ta-19', libelle: 'Brief de lancement du stand', projet: 'pj-pr-5', status: 'termine', priority: 'moyenne', assigne: CELIA, echeance: -5, tempsAlloue: 60 },
  { demoKey: 'pj-ta-20', libelle: 'Plan du stand', projet: 'pj-pr-5', status: 'aFaire', priority: 'moyenne', assigne: CELIA, etiquettes: ['design'], tempsAlloue: 120 },
  /* Newsletter (2), intranet (1), video close (1) */
  { demoKey: 'pj-ta-21', libelle: 'Gabarit de la newsletter', projet: 'pj-pr-6', status: 'aFaire', priority: 'moyenne', assigne: MARC, echeance: 19, etiquettes: ['design'], tempsAlloue: 180 },
  { demoKey: 'pj-ta-22', libelle: 'Calendrier éditorial', projet: 'pj-pr-6', status: 'aFaire', priority: 'faible', tempsAlloue: 60 },
  { demoKey: 'pj-ta-23', libelle: "Recette de l'intranet", projet: 'pj-pr-7', status: 'termine', priority: 'moyenne', assigne: CELIA, echeance: -14, validation: 'approuvee', etiquettes: ['interne'], tempsAlloue: 120 },
  { demoKey: 'pj-ta-24', libelle: 'Export final et archivage', projet: 'pj-pr-8', status: 'termine', priority: 'faible', assigne: MARC, echeance: -61, validation: 'approuvee', tempsAlloue: 45 },
  /* SANS PROJET (6) : les taches de l'agence elle-meme, dont une en retard. */
  { demoKey: 'pj-ta-25', libelle: 'Relancer les factures de septembre', status: 'aFaire', priority: 'elevee', assigne: CELIA, echeance: -2, etiquettes: ['interne'], tempsAlloue: 30 },
  { demoKey: 'pj-ta-26', libelle: 'Renouveler les licences Adobe', status: 'aFaire', priority: 'urgente', assigne: CELIA, echeance: 4, etiquettes: ['interne'], tempsAlloue: 20 },
  { demoKey: 'pj-ta-27', libelle: "Préparer l'entretien annuel", status: 'enCours', priority: 'moyenne', assigne: CELIA, echeance: 10, etiquettes: ['interne'], tempsAlloue: 60 },
  { demoKey: 'pj-ta-28', libelle: 'Trier la photothèque', status: 'aFaire', priority: 'faible', assigne: CELIA, etiquettes: ['interne'], tempsAlloue: 120 },
  { demoKey: 'pj-ta-29', libelle: 'Mettre à jour le portfolio', status: 'enCours', priority: 'moyenne', assigne: CELIA, echeance: 16, etiquettes: ['interne', 'evolution'], tempsAlloue: 180 },
  { demoKey: 'pj-ta-30', libelle: "Corriger le lien cassé du site de l'agence", status: 'aFaire', priority: 'elevee', assigne: CELIA, echeance: 1, etiquettes: ['interne', 'bug'], tempsAlloue: 30 },
];

/** Le nombre total de lignes que ce jeu pose, cite par le hub Parametres. */
export const DEMO_TOTAL = DEMO_CLIENTS.length + DEMO_PROJETS.length + DEMO_TACHES.length;

/* ------------------------------------------------------------------ */
/* Les dates, en heure locale                                          */
/* ------------------------------------------------------------------ */

/** Le lundi de la semaine de `reference`, a minuit LOCAL. */
function lundiDeLaSemaine(reference: Date): Date {
  const lundi = new Date(reference.getFullYear(), reference.getMonth(), reference.getDate());
  lundi.setDate(lundi.getDate() - ((lundi.getDay() + 6) % 7));
  return lundi;
}

/**
 * MIDI LOCAL, le jour dit : un debut ou une echeance est un jour, pas un
 * rendez-vous, et midi tient jusqu'a onze heures d'ecart de fuseau.
 */
function midiLocal(lundi: Date, jours: number): Date {
  return new Date(lundi.getFullYear(), lundi.getMonth(), lundi.getDate() + jours, 12, 0, 0, 0);
}

/* ------------------------------------------------------------------ */
/* L'import                                                            */
/* ------------------------------------------------------------------ */

/** Les `demoKey` deja en base pour une liste de fiches. */
function clefsConnues(fiches: ReadonlyArray<{ customFields?: Record<string, unknown> }>): Set<string> {
  const connues = new Set<string>();
  for (const fiche of fiches) {
    const clef = fiche.customFields?.['demoKey'];
    if (typeof clef === 'string') connues.add(clef);
  }
  return connues;
}

/** La table `demoKey -> id` d'une liste deja en base. */
function idsParClef(
  fiches: ReadonlyArray<{ id: string; customFields?: Record<string, unknown> }>,
): Map<string, string> {
  const table = new Map<string, string>();
  for (const fiche of fiches) {
    const clef = fiche.customFields?.['demoKey'];
    if (typeof clef === 'string') table.set(clef, fiche.id);
  }
  return table;
}

/** Un sac sans clef vide : un champ sans valeur s'OMET, il ne se met pas a `null`. */
function sans<T extends Record<string, unknown>>(sac: T): CustomFieldValues {
  return Object.fromEntries(
    Object.entries(sac).filter(([, valeur]) => valeur !== undefined),
  ) as CustomFieldValues;
}

const lignes = (n: number): string => `${n} ${n > 1 ? 'lignes' : 'ligne'}`;

/**
 * ECRIRE LE JEU DANS L'ORGANISATION COURANTE, et ne creer que ce qui manque.
 * Les clients, puis les projets, puis les taches : chaque jeu se relit apres
 * ecriture pour donner ses id reels au suivant.
 */
export async function importDemoDataset(sdk: TakiSdk, orgId: string): Promise<DemoImportResult> {
  let created = 0;
  let skipped = 0;
  const lundi = lundiDeLaSemaine(new Date());

  /* LES PERSONNES DE L'ANNUAIRE, par adresse. Une adresse inconnue (une org
     sans ces comptes) laisse le champ VIDE, elle ne s'invente pas. */
  const parAdresse = new Map(
    (await sdk.personnes.list({ archived: 'all' })).map((personne) => [
      personne.email.toLowerCase(),
      personne.personId,
    ]),
  );
  const personne = (adresse: string | undefined): string | undefined =>
    adresse === undefined ? undefined : parAdresse.get(adresse);

  /* ---- 1. Les clients (contacts::organization) ---- */
  const clientsConnus = clefsConnues(await sdk.contacts.list({ type: TYPE_SOCIETE }));
  for (const item of DEMO_CLIENTS) {
    if (clientsConnus.has(item.demoKey)) {
      skipped += 1;
      continue;
    }
    await sdk.contacts.create({
      orgId,
      type: TYPE_SOCIETE,
      name: item.name,
      email: item.email,
      phone: item.phone,
      customFields: { relation: 'client', demoKey: item.demoKey },
    });
    created += 1;
  }
  const clients = idsParClef(await sdk.contacts.list({ type: TYPE_SOCIETE }));

  /* ---- 2. Les projets (projects::projet) ---- */
  const projetsConnus = clefsConnues(await sdk.projects.list({ type: TYPE_PROJET }));
  for (const item of DEMO_PROJETS) {
    if (projetsConnus.has(item.demoKey)) {
      skipped += 1;
      continue;
    }
    const contactId = item.client === undefined ? undefined : clients.get(item.client);
    await sdk.projects.create({
      orgId,
      type: TYPE_PROJET,
      ...(contactId === undefined ? {} : { contactId }),
      startAt: midiLocal(lundi, item.debut),
      dueAt: midiLocal(lundi, item.echeance),
      budgetHours: item.budgetHeures,
      ...(item.budget === undefined ? {} : { budgetAmount: item.budget }),
      customFields: sans({
        libelle: item.libelle,
        etat: item.etat,
        statutSuivi: item.statutSuivi,
        avancement: item.avancement,
        emailClient: item.emailClient,
        chefDeProjet: personne(item.chef),
        etiquettes: item.etiquettes,
        facturable: item.facturable,
        description: item.description,
        demoKey: item.demoKey,
      }),
    });
    created += 1;
  }
  const projets = idsParClef(await sdk.projects.list({ type: TYPE_PROJET }));

  /* ---- 3. Les taches (tasks::tache) ---- */
  const tachesConnues = clefsConnues(await sdk.tasks.list({ type: TYPE_TACHE }));
  for (const item of DEMO_TACHES) {
    if (tachesConnues.has(item.demoKey)) {
      skipped += 1;
      continue;
    }
    const projectId = item.projet === undefined ? undefined : projets.get(item.projet);
    await sdk.tasks.create({
      orgId,
      type: TYPE_TACHE,
      status: item.status,
      priority: item.priority,
      ...(projectId === undefined ? {} : { projectId }),
      ...(item.echeance === undefined ? {} : { dueAt: midiLocal(lundi, item.echeance) }),
      customFields: sans({
        libelle: item.libelle,
        assigne: personne(item.assigne),
        validation: item.validation,
        etiquettes: item.etiquettes,
        tempsAlloue: item.tempsAlloue,
        demoKey: item.demoKey,
      }),
    });
    created += 1;
  }

  const dejaLa =
    skipped === 0 ? "rien n'existait déjà" : `${skipped} déjà ${skipped > 1 ? 'présentes' : 'présente'}`;
  const message =
    created === 0
      ? `Le jeu de démonstration est déjà en place (${lignes(skipped)}).`
      : `${lignes(created)} ${created > 1 ? 'créées' : 'créée'}, ${dejaLa}.`;
  return { created, skipped, message };
}
